import { ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '@/lib/api-client';
import type { Child } from '@/lib/auth-types';
import { ASSETS } from '@/lib/config';
import { STRINGS } from '@/strings/strings';

interface WorldCard {
  slug: 'aa' | 'sb' | 'kp';
  name: string;
  description: string;
  illustration: string;
  illustrationAlt: string;
  bg: string;
  border: string;
}

export function WorldSelectionRoute() {
  const navigate = useNavigate();
  const [childName, setChildName] = useState<string | null>(null);
  const [pendingSlug, setPendingSlug] = useState<WorldCard['slug'] | null>(null);
  const S = STRINGS.onboarding.world;

  useEffect(() => {
    apiFetch<Child[]>('/api/children')
      .then((children) => {
        const first = children[0];
        if (first) {
          setChildName(first.name);
        }
      })
      .catch((err) => {
        console.error('[WorldSelection] failed to load children:', err);
      });
  }, []);

  const title = childName ? S.titleTemplate.replace('{childName}', childName) : S.titleFallback;
  const subtext = childName
    ? S.heroSubtextTemplate.replace('{childName}', childName)
    : S.heroSubtextFallback;

  const worldCards: WorldCard[] = [
    {
      slug: 'aa',
      name: S.aaName,
      description: S.aaDescription,
      illustration: ASSETS.aaFirefighterIllustration,
      illustrationAlt: S.aaIllustrationAlt,
      bg: 'rgba(125, 185, 255, 0.15)',
      border: 'rgba(125, 185, 255, 0.3)',
    },
    {
      slug: 'sb',
      name: S.sbName,
      description: S.sbDescription,
      illustration: ASSETS.sbSchlaubergerFoxIllustration,
      illustrationAlt: S.sbIllustrationAlt,
      bg: 'rgba(255, 244, 222, 0.2)',
      border: 'rgba(255, 244, 222, 0.4)',
    },
    {
      slug: 'kp',
      name: S.kpName,
      description: S.kpDescription,
      illustration: ASSETS.kaptnKuschelImage,
      illustrationAlt: S.kpIllustrationAlt,
      bg: 'rgba(200, 184, 255, 0.15)',
      border: 'rgba(200, 184, 255, 0.3)',
    },
  ];

  const handleSelect = (slug: WorldCard['slug']) => {
    if (pendingSlug) return;
    setPendingSlug(slug);
    navigate(`/onboarding/how-it-works/${slug}`);
  };

  return (
    <>
      <h1 className="text-[20px] font-semibold text-dream-white mb-6 leading-snug">{title}</h1>

      <div className="flex flex-col gap-3">
        {worldCards.map((card) => (
          <button
            key={card.slug}
            type="button"
            disabled={Boolean(pendingSlug)}
            onClick={() => handleSelect(card.slug)}
            style={{ backgroundColor: card.bg, borderColor: card.border }}
            className="w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-[filter] hover:brightness-95 active:brightness-90 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <img
              src={card.illustration}
              alt={card.illustrationAlt}
              loading="eager"
              className="w-20 h-20 object-contain shrink-0"
            />
            <div className="flex-1 min-w-0">
              <h2 className="text-base font-semibold text-dream-white">{card.name}</h2>
              <p className="text-xs text-dream-white/90 mt-1 leading-relaxed">{card.description}</p>
              <p className="text-xs italic text-dream-white/70 mt-1">{subtext}</p>
            </div>
            <ChevronRight className="w-5 h-5 shrink-0 text-dream-white/50" />
          </button>
        ))}
      </div>
    </>
  );
}
